import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseQuery } from "@/api/base-query";
import { API_ENDPOINTS } from "@/api/config";
import { ROLE_PERMISSIONS } from "@/features/role-permissions/role-permissions";
import { authorizeRuntimePermission } from "@/features/role-permissions/runtime-permission-guard";
import {
  showErrorNotification,
  showSuccessNotification,
} from "@/store/common/commonSlice";
import type { RootState } from "@/store/types";
import type {
  BulkDeleteFoldersRequest,
  BulkDeleteFoldersResponse,
  BulkFileKeysRequest,
  BulkMoveFilesToFolderRequest,
  BulkMoveFilesToFolderResponse,
  BulkMoveFilesToTrashResponse,
  BulkMoveFoldersRequest,
  BulkMoveFoldersResponse,
  BulkPurgeFilesResponse,
  BulkRestoreFilesFromTrashResponse,
  ContentApiResponse,
  CreateFolderRequest,
  CreateFolderResponse,
  DeleteFolderRequest,
  DeleteFolderResponse,
  FileKeyRequest,
  ListFilesRequest,
  ListFilesResponse,
  ListFoldersRequest,
  ListFoldersResponse,
  ListTrashFilesRequest,
  ListTrashFilesResponse,
  MoveFileToFolderRequest,
  MoveFileToFolderResponse,
  MoveFileToTrashResponse,
  MoveFolderRequest,
  MoveFolderResponse,
  PurgeFileResponse,
  RenameFolderRequest,
  RenameFolderResponse,
  RestoreFileFromTrashResponse,
  StorageUsageResponse,
} from "./files.types";

const contentBaseQuery = createBaseQuery("contentApi");
const CONTENT_ENDPOINTS = API_ENDPOINTS.content;
const FOLDER_NAME_MAX_LENGTH = 120;
const ROOT_FOLDER_LIST_TAG_ID = "LIST:ROOT";
const ALL_FOLDER_LISTS_TAG_ID = "LIST:ALL";
const FILE_LIST_TAG_ID = "LIST";
const TRASH_LIST_TAG_ID = "LIST";
const STORAGE_USAGE_TAG_ID = "USAGE";

type ContentTagType =
  | "ContentFolder"
  | "ContentFile"
  | "ContentTrash"
  | "ContentStorage";
type ApiDispatcher = { dispatch: (action: unknown) => void };

const createApiError = (status: number | string, message: string) => ({
  error: {
    status,
    data: { message },
  },
});

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error && typeof error === "object") {
    const data = (error as { data?: { message?: unknown } }).data;
    if (typeof data?.message === "string" && data.message.trim()) {
      return data.message;
    }
  }
  return fallback;
};

const getResponseErrorMessage = <T>(
  response: ContentApiResponse<T> | undefined,
  fallback: string,
): string | null => {
  if (response?.success) return null;
  return response?.message || fallback;
};

const validateRequiredKey = (
  value: unknown,
  fieldLabel: string,
): string | null => {
  if (typeof value !== "string" || value.trim().length === 0) {
    return `${fieldLabel} is required.`;
  }
  return null;
};

const normalizeKeys = (values: string[] | undefined): string[] =>
  Array.from(
    new Set(
      (values ?? [])
        .map((value) => value.trim())
        .filter((value) => value.length > 0),
    ),
  );

const validateRequiredKeys = (
  values: string[] | undefined,
  fieldLabel: string,
): string | null => {
  if (normalizeKeys(values).length === 0) {
    return `${fieldLabel} are required.`;
  }
  return null;
};

const buildQueryString = (
  params: Record<string, string | number | boolean | undefined>,
): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === "") return;
    searchParams.set(key, String(value));
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : "";
};

const getTrashFilesFromResponse = (
  response: ListTrashFilesResponse | undefined,
) => {
  if (!response?.data) return [];
  return Array.isArray(response.data) ? response.data : response.data.items;
};

const getFolderListTagId = (parentFolderKey?: string): string =>
  parentFolderKey?.trim()
    ? `LIST:${parentFolderKey.trim()}`
    : ROOT_FOLDER_LIST_TAG_ID;

const entityTag = <T extends ContentTagType>(type: T, id: string) =>
  ({ type, id }) as const;

const notifyError = (
  api: ApiDispatcher,
  message: string,
) => {
  api.dispatch(
    showErrorNotification({
      title: "Error",
      message,
    }),
  );
};

const notifyResultError = (
  api: ApiDispatcher,
  error: unknown,
  fallback: string,
) => {
  notifyError(api, getErrorMessage(error, fallback));
};

const notifySuccess = (
  api: ApiDispatcher,
  message: string,
) => {
  api.dispatch(
    showSuccessNotification({
      title: "Success",
      message,
    }),
  );
};

export const validateFolderName = (name: unknown): string | null => {
  if (typeof name !== "string" || name.trim().length === 0) {
    return "Folder name is required.";
  }

  const normalizedName = name.trim();

  if (normalizedName.length > FOLDER_NAME_MAX_LENGTH) {
    return `Folder name must be ${FOLDER_NAME_MAX_LENGTH} characters or fewer.`;
  }

  if (normalizedName === "." || normalizedName === "..") {
    return "Folder name is invalid.";
  }

  if (/[\\/]/.test(normalizedName)) {
    return "Folder name cannot contain slash characters.";
  }

  return null;
};

export const filesApi = createApi({
  reducerPath: "filesApi",
  baseQuery: contentBaseQuery,
  tagTypes: ["ContentFolder", "ContentFile", "ContentTrash", "ContentStorage"],
  keepUnusedDataFor: 30,
  refetchOnReconnect: true,
  refetchOnFocus: false,
  endpoints: (builder) => ({
    listFolders: builder.query<ListFoldersResponse, ListFoldersRequest | void>({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const permission = authorizeRuntimePermission(
          api.getState() as RootState,
          ROLE_PERMISSIONS.ACCOUNT_FILES_READ,
        );
        if (permission !== true) return permission;

        const parentFolderKey = input?.parentFolderKey?.trim();
        const result = await baseQuery({
          url: `${CONTENT_ENDPOINTS.folders}${buildQueryString({
            parentFolderKey,
            includeHierarchy: true,
          })}`,
          method: "GET",
        });

        if (result.error) return { error: result.error };

        const response = result.data as ListFoldersResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to load folders.",
        );
        if (responseError) return createApiError(400, responseError);

        return { data: response };
      },
      providesTags: (result, _error, input) => {
        const parentFolderKey = input?.parentFolderKey?.trim();
        const listTags = [
          entityTag("ContentFolder", ALL_FOLDER_LISTS_TAG_ID),
          entityTag("ContentFolder", getFolderListTagId(parentFolderKey)),
        ];

        return result?.data?.length
          ? [
              ...result.data.map((folder) =>
                entityTag("ContentFolder", folder.folderKey),
              ),
              ...listTags,
            ]
          : listTags;
      },
    }),

    getStorageUsage: builder.query<StorageUsageResponse, void>({
      queryFn: async (_input, api, _extraOptions, baseQuery) => {
        const permission = authorizeRuntimePermission(
          api.getState() as RootState,
          ROLE_PERMISSIONS.ACCOUNT_FILES_METRICS,
        );
        if (permission !== true) return permission;

        const result = await baseQuery({
          url: CONTENT_ENDPOINTS.storageUsage,
          method: "GET",
        });

        if (result.error) return { error: result.error };

        const response = result.data as StorageUsageResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to load storage usage.",
        );
        if (responseError) return createApiError(400, responseError);

        return { data: response };
      },
      providesTags: [entityTag("ContentStorage", STORAGE_USAGE_TAG_ID)],
    }),

    createFolder: builder.mutation<CreateFolderResponse, CreateFolderRequest>({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const permission = authorizeRuntimePermission(
          api.getState() as RootState,
          ROLE_PERMISSIONS.ACCOUNT_FILES_WRITE,
        );
        if (permission !== true) return permission;

        const nameError = validateFolderName(input.name);
        if (nameError) return createApiError(400, nameError);

        const parentFolderKey = input.parentFolderKey?.trim();
        const result = await baseQuery({
          url: CONTENT_ENDPOINTS.folders,
          method: "POST",
          body: {
            name: input.name.trim(),
            ...(parentFolderKey ? { parentFolderKey } : {}),
          },
        });

        if (result.error) {
          notifyResultError(api, result.error, "Failed to create folder.");
          return { error: result.error };
        }

        const response = result.data as CreateFolderResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to create folder.",
        );
        if (responseError) {
          notifyError(api, responseError);
          return createApiError(400, responseError);
        }

        notifySuccess(
          api,
          response.message || "Folder created successfully.",
        );

        return { data: response };
      },
      invalidatesTags: (_result, _error, input) => [
        entityTag("ContentFolder", ALL_FOLDER_LISTS_TAG_ID),
        entityTag("ContentFolder", getFolderListTagId(input.parentFolderKey)),
      ],
    }),

    renameFolder: builder.mutation<RenameFolderResponse, RenameFolderRequest>({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const permission = authorizeRuntimePermission(
          api.getState() as RootState,
          ROLE_PERMISSIONS.ACCOUNT_FILES_WRITE,
        );
        if (permission !== true) return permission;

        const folderKey = input.folderKey?.trim();
        const folderKeyError = validateRequiredKey(folderKey, "Folder key");
        if (folderKeyError) return createApiError(400, folderKeyError);

        const nameError = validateFolderName(input.name);
        if (nameError) return createApiError(400, nameError);

        const result = await baseQuery({
          url: CONTENT_ENDPOINTS.renameFolder(folderKey),
          method: "POST",
          body: { name: input.name.trim() },
        });

        if (result.error) {
          notifyResultError(api, result.error, "Failed to rename folder.");
          return { error: result.error };
        }

        const response = result.data as RenameFolderResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to rename folder.",
        );
        if (responseError) {
          notifyError(api, responseError);
          return createApiError(400, responseError);
        }

        notifySuccess(
          api,
          response.message || "Folder renamed successfully.",
        );

        return { data: response };
      },
      invalidatesTags: (result, _error, input) => [
        entityTag("ContentFolder", input.folderKey),
        entityTag("ContentFolder", ALL_FOLDER_LISTS_TAG_ID),
        entityTag(
          "ContentFolder",
          getFolderListTagId(result?.data.parentFolderKey ?? undefined),
        ),
      ],
    }),

    moveFolder: builder.mutation<MoveFolderResponse, MoveFolderRequest>({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const permission = authorizeRuntimePermission(
          api.getState() as RootState,
          ROLE_PERMISSIONS.ACCOUNT_FILES_WRITE,
        );
        if (permission !== true) return permission;

        const folderKey = input.folderKey?.trim();
        const destinationFolderKey = input.destinationFolderKey?.trim();
        const folderKeyError = validateRequiredKey(folderKey, "Folder key");
        if (folderKeyError) return createApiError(400, folderKeyError);

        const destinationError = validateRequiredKey(
          destinationFolderKey,
          "Destination folder key",
        );
        if (destinationError) return createApiError(400, destinationError);

        const result = await baseQuery({
          url: CONTENT_ENDPOINTS.moveFolder(folderKey),
          method: "POST",
          body: { destinationFolderKey },
        });

        if (result.error) {
          notifyResultError(api, result.error, "Failed to move folder.");
          return { error: result.error };
        }

        const response = result.data as MoveFolderResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to move folder.",
        );
        if (responseError) {
          notifyError(api, responseError);
          return createApiError(400, responseError);
        }

        notifySuccess(api, response.message || "Folder moved successfully.");

        return { data: response };
      },
      invalidatesTags: (_result, _error, input) => [
        entityTag("ContentFolder", input.folderKey),
        entityTag("ContentFolder", ALL_FOLDER_LISTS_TAG_ID),
        entityTag(
          "ContentFolder",
          getFolderListTagId(input.destinationFolderKey),
        ),
      ],
    }),

    bulkMoveFolders: builder.mutation<
      BulkMoveFoldersResponse,
      BulkMoveFoldersRequest
    >({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const permission = authorizeRuntimePermission(
          api.getState() as RootState,
          ROLE_PERMISSIONS.ACCOUNT_FILES_WRITE,
        );
        if (permission !== true) return permission;

        const folderKeys = normalizeKeys(input.folderKeys);
        const destinationFolderKey = input.destinationFolderKey?.trim();
        const folderKeysError = validateRequiredKeys(folderKeys, "Folder keys");
        if (folderKeysError) return createApiError(400, folderKeysError);

        const destinationError = validateRequiredKey(
          destinationFolderKey,
          "Destination folder key",
        );
        if (destinationError) return createApiError(400, destinationError);

        const result = await baseQuery({
          url: CONTENT_ENDPOINTS.bulkMoveFolders,
          method: "POST",
          body: { folderKeys, destinationFolderKey },
        });

        if (result.error) {
          notifyResultError(api, result.error, "Failed to move folders.");
          return { error: result.error };
        }

        const response = result.data as BulkMoveFoldersResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to move folders.",
        );
        if (responseError) {
          notifyError(api, responseError);
          return createApiError(400, responseError);
        }

        notifySuccess(api, response.message || "Folders moved successfully.");

        return { data: response };
      },
      invalidatesTags: (_result, _error, input) => [
        ...normalizeKeys(input.folderKeys).map((folderKey) =>
          entityTag("ContentFolder", folderKey),
        ),
        entityTag("ContentFolder", ALL_FOLDER_LISTS_TAG_ID),
        entityTag(
          "ContentFolder",
          getFolderListTagId(input.destinationFolderKey),
        ),
      ],
    }),

    bulkDeleteFolders: builder.mutation<
      BulkDeleteFoldersResponse,
      BulkDeleteFoldersRequest
    >({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const permission = authorizeRuntimePermission(
          api.getState() as RootState,
          ROLE_PERMISSIONS.ACCOUNT_FILES_WRITE,
        );
        if (permission !== true) return permission;

        const folderKeys = normalizeKeys(input.folderKeys);
        const folderKeysError = validateRequiredKeys(folderKeys, "Folder keys");
        if (folderKeysError) return createApiError(400, folderKeysError);

        const result = await baseQuery({
          url: CONTENT_ENDPOINTS.bulkDeleteFolders,
          method: "POST",
          body: { folderKeys },
        });

        if (result.error) {
          notifyResultError(api, result.error, "Failed to delete folders.");
          return { error: result.error };
        }

        const response = result.data as BulkDeleteFoldersResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to delete folders.",
        );
        if (responseError) {
          notifyError(api, responseError);
          return createApiError(400, responseError);
        }

        notifySuccess(api, response.message || "Folders deleted successfully.");

        return { data: response };
      },
      invalidatesTags: (_result, _error, input) => [
        ...normalizeKeys(input.folderKeys).map((folderKey) =>
          entityTag("ContentFolder", folderKey),
        ),
        entityTag("ContentFolder", ALL_FOLDER_LISTS_TAG_ID),
        entityTag("ContentStorage", STORAGE_USAGE_TAG_ID),
      ],
    }),

    deleteFolder: builder.mutation<DeleteFolderResponse, DeleteFolderRequest>({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const permission = authorizeRuntimePermission(
          api.getState() as RootState,
          ROLE_PERMISSIONS.ACCOUNT_FILES_WRITE,
        );
        if (permission !== true) return permission;

        const folderKey = input.folderKey?.trim();
        const folderKeyError = validateRequiredKey(folderKey, "Folder key");
        if (folderKeyError) return createApiError(400, folderKeyError);

        const result = await baseQuery({
          url: `${CONTENT_ENDPOINTS.folderByKey(folderKey)}${buildQueryString({
            recursive: input.recursive,
          })}`,
          method: "DELETE",
        });

        if (result.error) {
          notifyResultError(api, result.error, "Failed to delete folder.");
          return { error: result.error };
        }

        const response = result.data as DeleteFolderResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to delete folder.",
        );
        if (responseError) {
          notifyError(api, responseError);
          return createApiError(400, responseError);
        }

        notifySuccess(api, response.message || "Folder deleted successfully.");

        return { data: response };
      },
      invalidatesTags: (_result, _error, input) => [
        entityTag("ContentFolder", input.folderKey),
        entityTag("ContentFolder", ALL_FOLDER_LISTS_TAG_ID),
        entityTag("ContentStorage", STORAGE_USAGE_TAG_ID),
      ],
    }),

    bulkMoveFilesToFolder: builder.mutation<
      BulkMoveFilesToFolderResponse,
      BulkMoveFilesToFolderRequest
    >({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const permission = authorizeRuntimePermission(
          api.getState() as RootState,
          ROLE_PERMISSIONS.ACCOUNT_FILES_WRITE,
        );
        if (permission !== true) return permission;

        const fileKeys = normalizeKeys(input.fileKeys);
        const destinationFolderKey = input.destinationFolderKey?.trim();
        const fileKeysError = validateRequiredKeys(fileKeys, "File keys");
        if (fileKeysError) return createApiError(400, fileKeysError);

        const destinationError = validateRequiredKey(
          destinationFolderKey,
          "Destination folder key",
        );
        if (destinationError) return createApiError(400, destinationError);

        const result = await baseQuery({
          url: CONTENT_ENDPOINTS.bulkMoveFilesToFolder,
          method: "POST",
          body: { fileKeys, destinationFolderKey },
        });

        if (result.error) {
          notifyResultError(api, result.error, "Failed to move files.");
          return { error: result.error };
        }

        const response = result.data as BulkMoveFilesToFolderResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to move files.",
        );
        if (responseError) {
          notifyError(api, responseError);
          return createApiError(400, responseError);
        }

        notifySuccess(api, response.message || "Files moved successfully.");

        return { data: response };
      },
      invalidatesTags: (_result, _error, input) => [
        ...normalizeKeys(input.fileKeys).map((fileKey) =>
          entityTag("ContentFile", fileKey),
        ),
        entityTag("ContentFile", FILE_LIST_TAG_ID),
        entityTag(
          "ContentFolder",
          getFolderListTagId(input.destinationFolderKey),
        ),
      ],
    }),

    moveFileToFolder: builder.mutation<
      MoveFileToFolderResponse,
      MoveFileToFolderRequest
    >({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const permission = authorizeRuntimePermission(
          api.getState() as RootState,
          ROLE_PERMISSIONS.ACCOUNT_FILES_WRITE,
        );
        if (permission !== true) return permission;

        const fileKey = input.fileKey?.trim();
        const destinationFolderKey = input.destinationFolderKey?.trim();
        const fileKeyError = validateRequiredKey(fileKey, "File key");
        if (fileKeyError) return createApiError(400, fileKeyError);

        const destinationError = validateRequiredKey(
          destinationFolderKey,
          "Destination folder key",
        );
        if (destinationError) return createApiError(400, destinationError);

        const result = await baseQuery({
          url: CONTENT_ENDPOINTS.moveFileToFolder(fileKey),
          method: "POST",
          body: { destinationFolderKey },
        });

        if (result.error) {
          notifyResultError(api, result.error, "Failed to move file.");
          return { error: result.error };
        }

        const response = result.data as MoveFileToFolderResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to move file.",
        );
        if (responseError) {
          notifyError(api, responseError);
          return createApiError(400, responseError);
        }

        notifySuccess(api, response.message || "File moved successfully.");

        return { data: response };
      },
      invalidatesTags: (_result, _error, input) => [
        entityTag("ContentFile", input.fileKey),
        entityTag("ContentFile", FILE_LIST_TAG_ID),
        entityTag(
          "ContentFolder",
          getFolderListTagId(input.destinationFolderKey),
        ),
      ],
    }),

    listFiles: builder.query<ListFilesResponse, ListFilesRequest | void>({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const permission = authorizeRuntimePermission(
          api.getState() as RootState,
          ROLE_PERMISSIONS.ACCOUNT_FILES_READ,
        );
        if (permission !== true) return permission;

        const result = await baseQuery({
          url: `${CONTENT_ENDPOINTS.files}${buildQueryString({
            folderKey: input?.folderKey,
            status: input?.status,
            visibility: input?.visibility,
            mediaType: input?.mediaType,
            sourceType: input?.sourceType,
            fromCreatedTs: input?.fromCreatedTs,
            toCreatedTs: input?.toCreatedTs,
            sort: input?.sort,
            order: input?.order,
            limit: input?.limit,
            cursor: input?.cursor,
            includeDeleted: input?.includeDeleted,
            includePreviews: input?.includePreviews,
            includeAccess: input?.includeAccess ?? true,
          })}`,
          method: "GET",
        });

        if (result.error) return { error: result.error };

        const response = result.data as ListFilesResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to load files.",
        );
        if (responseError) return createApiError(400, responseError);

        return { data: response };
      },
      providesTags: (result) =>
        result?.data?.items?.length
          ? [
              ...result.data.items.map((file) =>
                entityTag("ContentFile", file.fileKey),
              ),
              entityTag("ContentFile", FILE_LIST_TAG_ID),
            ]
          : [entityTag("ContentFile", FILE_LIST_TAG_ID)],
    }),

    listTrashFiles: builder.query<
      ListTrashFilesResponse,
      ListTrashFilesRequest | void
    >({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const permission = authorizeRuntimePermission(
          api.getState() as RootState,
          ROLE_PERMISSIONS.ACCOUNT_FILES_GOVERN,
        );
        if (permission !== true) return permission;

        const result = await baseQuery({
          url: `${CONTENT_ENDPOINTS.trashFiles}${buildQueryString({
            limit: input?.limit,
            cursor: input?.cursor,
            fromDeletedTs: input?.fromDeletedTs,
            toDeletedTs: input?.toDeletedTs,
          })}`,
          method: "GET",
        });

        if (result.error) return { error: result.error };

        const response = result.data as ListTrashFilesResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to load trash.",
        );
        if (responseError) return createApiError(400, responseError);

        return { data: response };
      },
      providesTags: (result) => {
        const files = getTrashFilesFromResponse(result);

        return files.length
          ? [
              ...files.map((file) =>
                entityTag("ContentTrash", file.fileKey),
              ),
              entityTag("ContentTrash", TRASH_LIST_TAG_ID),
            ]
          : [entityTag("ContentTrash", TRASH_LIST_TAG_ID)];
      },
    }),

    moveFileToTrash: builder.mutation<MoveFileToTrashResponse, FileKeyRequest>({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const permission = authorizeRuntimePermission(
          api.getState() as RootState,
          ROLE_PERMISSIONS.ACCOUNT_FILES_WRITE,
        );
        if (permission !== true) return permission;

        const fileKey = input.fileKey?.trim();
        const fileKeyError = validateRequiredKey(fileKey, "File key");
        if (fileKeyError) return createApiError(400, fileKeyError);

        const result = await baseQuery({
          url: CONTENT_ENDPOINTS.moveFileToTrash(fileKey),
          method: "POST",
        });

        if (result.error) {
          if (!input.suppressNotifications) {
            notifyResultError(api, result.error, "Failed to move file to trash.");
          }
          return { error: result.error };
        }

        const response = result.data as MoveFileToTrashResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to move file to trash.",
        );
        if (responseError) {
          if (!input.suppressNotifications) notifyError(api, responseError);
          return createApiError(400, responseError);
        }

        if (!input.suppressNotifications) {
          notifySuccess(api, response.message || "File moved to trash.");
        }

        return { data: response };
      },
      invalidatesTags: (_result, _error, input) => [
        entityTag("ContentFile", input.fileKey),
        entityTag("ContentFile", FILE_LIST_TAG_ID),
        entityTag("ContentTrash", TRASH_LIST_TAG_ID),
        entityTag("ContentStorage", STORAGE_USAGE_TAG_ID),
      ],
    }),

    bulkMoveFilesToTrash: builder.mutation<
      BulkMoveFilesToTrashResponse,
      BulkFileKeysRequest
    >({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const permission = authorizeRuntimePermission(
          api.getState() as RootState,
          ROLE_PERMISSIONS.ACCOUNT_FILES_WRITE,
        );
        if (permission !== true) return permission;

        const fileKeys = normalizeKeys(input.fileKeys);
        const fileKeysError = validateRequiredKeys(fileKeys, "File keys");
        if (fileKeysError) return createApiError(400, fileKeysError);

        const result = await baseQuery({
          url: CONTENT_ENDPOINTS.bulkMoveFilesToTrash,
          method: "POST",
          body: { fileKeys },
        });

        if (result.error) {
          notifyResultError(
            api,
            result.error,
            "Failed to move selected files to trash.",
          );
          return { error: result.error };
        }

        const response = result.data as BulkMoveFilesToTrashResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to move selected files to trash.",
        );
        if (responseError) {
          notifyError(api, responseError);
          return createApiError(400, responseError);
        }

        notifySuccess(
          api,
          response.message || "Selected files moved to trash.",
        );

        return { data: response };
      },
      invalidatesTags: (_result, _error, input) => [
        ...normalizeKeys(input.fileKeys).map((fileKey) =>
          entityTag("ContentFile", fileKey),
        ),
        entityTag("ContentFile", FILE_LIST_TAG_ID),
        entityTag("ContentTrash", TRASH_LIST_TAG_ID),
        entityTag("ContentStorage", STORAGE_USAGE_TAG_ID),
      ],
    }),

    restoreFileFromTrash: builder.mutation<
      RestoreFileFromTrashResponse,
      FileKeyRequest
    >({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const permission = authorizeRuntimePermission(
          api.getState() as RootState,
          ROLE_PERMISSIONS.ACCOUNT_FILES_WRITE,
        );
        if (permission !== true) return permission;

        const fileKey = input.fileKey?.trim();
        const fileKeyError = validateRequiredKey(fileKey, "File key");
        if (fileKeyError) return createApiError(400, fileKeyError);

        const result = await baseQuery({
          url: CONTENT_ENDPOINTS.restoreFileFromTrash(fileKey),
          method: "POST",
        });

        if (result.error) {
          notifyResultError(api, result.error, "Failed to restore file.");
          return { error: result.error };
        }

        const response = result.data as RestoreFileFromTrashResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to restore file.",
        );
        if (responseError) {
          notifyError(api, responseError);
          return createApiError(400, responseError);
        }

        notifySuccess(api, response.message || "File restored successfully.");

        return { data: response };
      },
      invalidatesTags: (_result, _error, input) => [
        entityTag("ContentFile", input.fileKey),
        entityTag("ContentFile", FILE_LIST_TAG_ID),
        entityTag("ContentTrash", input.fileKey),
        entityTag("ContentTrash", TRASH_LIST_TAG_ID),
        entityTag("ContentStorage", STORAGE_USAGE_TAG_ID),
      ],
    }),

    bulkRestoreFilesFromTrash: builder.mutation<
      BulkRestoreFilesFromTrashResponse,
      BulkFileKeysRequest
    >({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const permission = authorizeRuntimePermission(
          api.getState() as RootState,
          ROLE_PERMISSIONS.ACCOUNT_FILES_WRITE,
        );
        if (permission !== true) return permission;

        const fileKeys = normalizeKeys(input.fileKeys);
        const fileKeysError = validateRequiredKeys(fileKeys, "File keys");
        if (fileKeysError) return createApiError(400, fileKeysError);

        const result = await baseQuery({
          url: CONTENT_ENDPOINTS.bulkRestoreFilesFromTrash,
          method: "POST",
          body: { fileKeys },
        });

        if (result.error) {
          notifyResultError(
            api,
            result.error,
            "Failed to restore selected files.",
          );
          return { error: result.error };
        }

        const response = result.data as BulkRestoreFilesFromTrashResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to restore selected files.",
        );
        if (responseError) {
          notifyError(api, responseError);
          return createApiError(400, responseError);
        }

        notifySuccess(api, response.message || "Selected files restored.");

        return { data: response };
      },
      invalidatesTags: (_result, _error, input) => [
        ...normalizeKeys(input.fileKeys).flatMap((fileKey) => [
          entityTag("ContentFile", fileKey),
          entityTag("ContentTrash", fileKey),
        ]),
        entityTag("ContentFile", FILE_LIST_TAG_ID),
        entityTag("ContentTrash", TRASH_LIST_TAG_ID),
        entityTag("ContentStorage", STORAGE_USAGE_TAG_ID),
      ],
    }),

    bulkPurgeFiles: builder.mutation<
      BulkPurgeFilesResponse,
      BulkFileKeysRequest
    >({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const permission = authorizeRuntimePermission(
          api.getState() as RootState,
          ROLE_PERMISSIONS.ACCOUNT_FILES_GOVERN,
        );
        if (permission !== true) return permission;

        const fileKeys = normalizeKeys(input.fileKeys);
        const fileKeysError = validateRequiredKeys(fileKeys, "File keys");
        if (fileKeysError) return createApiError(400, fileKeysError);

        const result = await baseQuery({
          url: CONTENT_ENDPOINTS.bulkPurgeFiles,
          method: "POST",
          body: { fileKeys },
        });

        if (result.error) {
          notifyResultError(
            api,
            result.error,
            "Failed to permanently delete selected files.",
          );
          return { error: result.error };
        }

        const response = result.data as BulkPurgeFilesResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to permanently delete selected files.",
        );
        if (responseError) {
          notifyError(api, responseError);
          return createApiError(400, responseError);
        }

        notifySuccess(
          api,
          response.message || "Selected files deleted permanently.",
        );

        return { data: response };
      },
      invalidatesTags: (_result, _error, input) => [
        ...normalizeKeys(input.fileKeys).flatMap((fileKey) => [
          entityTag("ContentFile", fileKey),
          entityTag("ContentTrash", fileKey),
        ]),
        entityTag("ContentFile", FILE_LIST_TAG_ID),
        entityTag("ContentTrash", TRASH_LIST_TAG_ID),
        entityTag("ContentStorage", STORAGE_USAGE_TAG_ID),
      ],
    }),

    purgeFile: builder.mutation<PurgeFileResponse, FileKeyRequest>({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const permission = authorizeRuntimePermission(
          api.getState() as RootState,
          ROLE_PERMISSIONS.ACCOUNT_FILES_GOVERN,
        );
        if (permission !== true) return permission;

        const fileKey = input.fileKey?.trim();
        const fileKeyError = validateRequiredKey(fileKey, "File key");
        if (fileKeyError) return createApiError(400, fileKeyError);

        const result = await baseQuery({
          url: CONTENT_ENDPOINTS.purgeFile(fileKey),
          method: "DELETE",
        });

        if (result.error) {
          notifyResultError(api, result.error, "Failed to delete file.");
          return { error: result.error };
        }

        const response = result.data as PurgeFileResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to delete file.",
        );
        if (responseError) {
          notifyError(api, responseError);
          return createApiError(400, responseError);
        }

        notifySuccess(api, response.message || "File deleted permanently.");

        return { data: response };
      },
      invalidatesTags: (_result, _error, input) => [
        entityTag("ContentTrash", input.fileKey),
        entityTag("ContentTrash", TRASH_LIST_TAG_ID),
        entityTag("ContentStorage", STORAGE_USAGE_TAG_ID),
      ],
    }),
  }),
});

export const {
  useBulkDeleteFoldersMutation,
  useBulkMoveFilesToFolderMutation,
  useBulkMoveFilesToTrashMutation,
  useBulkMoveFoldersMutation,
  useBulkPurgeFilesMutation,
  useBulkRestoreFilesFromTrashMutation,
  useCreateFolderMutation,
  useDeleteFolderMutation,
  useGetStorageUsageQuery,
  useListFilesQuery,
  useListFoldersQuery,
  useListTrashFilesQuery,
  useMoveFileToFolderMutation,
  useMoveFileToTrashMutation,
  useMoveFolderMutation,
  usePurgeFileMutation,
  useRenameFolderMutation,
  useRestoreFileFromTrashMutation,
} = filesApi;
